/**
 * First-person movement, collision, and the camera.
 *
 * Collision is hand-written rather than delegated to a physics engine. What the
 * game actually needs is capsule-versus-vertical-cylinder resolution against
 * nearby trunks plus a terrain height clamp — a couple of hundred lines that
 * are exact, debuggable, and produce zero drift against the server's view of
 * the world. A physics engine would add a wasm payload and a second simulation
 * to keep in sync, in exchange for solving a problem we do not have.
 */

import * as THREE from 'three';
import {
  CAMERA,
  PLAYER,
  TOWERS,
  CONFRONT,
  MONSTER,
  Stance,
  angleDelta,
  yawToward,
  clamp,
  clamp01,
  damp,
  profileFor,
  type DifficultyProfile,
  type World,
} from '@static-pines/shared';

import { IS_TOUCH_DEVICE, type Input } from './Input.js';

const MAX_PITCH = Math.PI / 2 - 0.05;

/** Height up the creature that a forced confrontation aims at. */
const MONSTER_AIM_HEIGHT = MONSTER.HEIGHT * CONFRONT.AIM_HEIGHT;

export interface PlayerSnapshot {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  stance: number;
  speed: number;
  grounded: boolean;
}

export class LocalPlayer {
  x: number;
  y: number;
  z: number;
  yaw = 0;
  pitch = 0;

  velocityY = 0;
  grounded = true;
  // Annotated as number: the tuning table is `as const`, so inference from the
  // initialiser would pin this to the literal type.
  stamina: number = PLAYER.STAMINA_MAX;

  /** Set while climbing a tower ladder; -1 when not on one. */
  towerId = -1;
  climbing = false;

  /**
   * Set while inside a hiding place; movement is disabled.
   *
   * Read-only from outside, because getting in and out moves you: see
   * enterHiding and leaveHiding, which assigning this directly would skip.
   */
  private hideSpotId = -1;
  /**
   * Where you were standing when you ducked in.
   *
   * Kept so that leaving puts you back outside the spot rather than in the
   * middle of its geometry, staring at the inside of a bush.
   */
  private hideAnchor: { x: number; y: number; z: number } | null = null;

  /** Active forced confrontation: control is gone and the head turns itself. */
  private lookLock: {
    x: number;
    y: number;
    z: number;
    remaining: number;
    total: number;
  } | null = null;

  private stance: Stance = Stance.Standing;
  private eyeHeight: number = PLAYER.EYE_HEIGHT;
  /** Damped distance the view sits behind the head. 0 is first person. */
  private cameraBack = 0;
  private bobPhase = 0;
  private bobAmount = 0;
  private lastSpeed = 0;
  private sprintCooldown = 0;
  /** True after running the bar to zero, until STAMINA_EXHAUSTED_RECOVERY. */
  exhausted = false;

  /**
   * Taken from the world rather than passed in, because the world already
   * carries the difficulty it was built at — and stamina is simulated here on
   * the client, so this is the only place the tier can be read.
   */
  private readonly profile: DifficultyProfile;

  constructor(
    private readonly world: World,
    spawn: { x: number; y: number; z: number },
  ) {
    this.x = spawn.x;
    this.y = world.heightAt(spawn.x, spawn.z);
    this.z = spawn.z;
    this.profile = profileFor(world.difficulty);
  }

  get currentStance(): Stance {
    return this.stance;
  }

  get speed(): number {
    return this.lastSpeed;
  }

  teleport(x: number, y: number, z: number): void {
    this.x = x;
    this.y = y;
    this.z = z;
    this.velocityY = 0;
  }

  /**
   * Take control away and turn the head to face a world position.
   *
   * Aimed high up the creature rather than at his base — being made to crane
   * upward is most of what sells the scale.
   */
  beginLookLock(x: number, y: number, z: number, duration: number): void {
    this.lookLock = { x, y, z, remaining: duration, total: duration };
    this.velocityY = 0;
  }

  get isLookLocked(): boolean {
    return this.lookLock !== null;
  }

  /** 0 at the start of a confrontation, 1 at the end. */
  get lookLockProgress(): number {
    if (!this.lookLock) return 0;
    return clamp01(1 - this.lookLock.remaining / this.lookLock.total);
  }

  update(dt: number, input: Input, camera: THREE.PerspectiveCamera): void {
    /**
     * Pull the view back in the two states that take movement away.
     *
     * Driven off the same yaw and pitch first person uses, so the mouse still
     * steers a look direction rather than a separate orbit: the server keeps
     * receiving a meaningful facing, teammates still see the body and torch
     * pointed where you are looking, and dropping back to first person is
     * simply this distance returning to zero.
     *
     * A confrontation overrides both. Being shown the thing that is about to
     * kill you belongs behind your own eyes, and updateLookLock places the
     * camera there directly — so the offset has to be on its way to zero or
     * the torch would be left compensating for a camera that already moved.
     */
    const back = this.lookLock
      ? 0
      : this.hideSpotId >= 0
        ? CAMERA.HIDING_DISTANCE
        : this.climbing && this.towerId >= 0
          ? CAMERA.CLIMB_DISTANCE
          : 0;
    this.cameraBack = damp(this.cameraBack, back, CAMERA.TRANSITION_RATE, dt);

    if (this.lookLock) {
      // Drain accumulated mouse movement, or it snaps the view the instant
      // control returns.
      input.consumeLook();
      this.updateLookLock(dt, camera);
      return;
    }

    this.applyLook(input);

    if (this.hideSpotId >= 0) {
      this.updateHidden(dt, camera);
      return;
    }

    if (this.climbing && this.towerId >= 0) {
      this.updateClimbing(dt, input, camera);
      return;
    }

    this.updateGround(dt, input, camera);
  }

  private updateLookLock(dt: number, camera: THREE.PerspectiveCamera): void {
    const lock = this.lookLock!;
    lock.remaining -= dt;

    this.stance = Stance.Standing;
    this.lastSpeed = 0;

    // Still obey gravity — freezing mid-air and hovering would look absurd.
    const groundY = this.world.heightAt(this.x, this.z);
    if (this.y > groundY) {
      this.velocityY -= PLAYER.GRAVITY * dt;
      this.y = Math.max(groundY, this.y + this.velocityY * dt);
    } else {
      this.y = groundY;
      this.velocityY = 0;
    }

    const eyeY = this.y + PLAYER.EYE_HEIGHT;
    const targetY = lock.y + MONSTER_AIM_HEIGHT;

    const dx = lock.x - this.x;
    const dz = lock.z - this.z;
    const horizontal = Math.hypot(dx, dz);

    // yawToward, not atan2(dx, dz): the latter is the monster's convention and
    // would turn the player's head a half-turn away from him.
    const desiredYaw = yawToward(this.x, this.z, lock.x, lock.z);
    const desiredPitch = Math.atan2(targetY - eyeY, Math.max(horizontal, 0.001));

    // Exponential approach tuned to land within TURN_TIME. Wrap-aware on yaw,
    // so a target behind you turns the short way round.
    const rate = 4.6 / CONFRONT.TURN_TIME;
    const t = 1 - Math.exp(-rate * dt);
    this.yaw += angleDelta(this.yaw, desiredYaw) * t;
    this.pitch += (desiredPitch - this.pitch) * t;

    this.eyeHeight = damp(this.eyeHeight, PLAYER.EYE_HEIGHT, 10, dt);

    camera.position.set(this.x, this.y + this.eyeHeight, this.z);
    camera.rotation.order = 'YXZ';
    camera.rotation.y = this.yaw;
    camera.rotation.x = this.pitch;
    // A fine tremor. Your hands are not steady.
    camera.rotation.z = Math.sin(lock.remaining * 34) * 0.012;

    if (lock.remaining <= 0) this.lookLock = null;
  }

  private applyLook(input: Input): void {
    const look = input.consumeLook();
    this.yaw += look.yaw;
    this.pitch = clamp(this.pitch + look.pitch, -MAX_PITCH, MAX_PITCH);
  }

  // -------------------------------------------------------------------------
  // Grounded movement
  // -------------------------------------------------------------------------

  private updateGround(dt: number, input: Input, camera: THREE.PerspectiveCamera): void {
    const s = input.sample();

    // Stance resolution. Sprinting requires stamina and forward intent, so you
    // cannot sprint sideways out of a chase.
    // Exhaustion clears the moment you have recovered enough, so a short
    // sprint never costs you a full bar's wait.
    if (this.exhausted && this.stamina >= PLAYER.STAMINA_EXHAUSTED_RECOVERY) {
      this.exhausted = false;
    }

    /**
     * Starting and continuing a sprint use different thresholds.
     *
     * A single threshold for both means the sprint cuts out at the floor, the
     * bar never actually reaches zero, and the exhaustion lock below can never
     * trigger. You may run until the bar is empty; you just cannot *start*
     * from almost nothing.
     */
    const alreadySprinting = this.stance === Stance.Sprinting;
    const staminaAllows = alreadySprinting
      ? this.stamina > 0
      : this.stamina > PLAYER.STAMINA_SPRINT_FLOOR;

    const wantsSprint = s.sprint && s.forward > 0 && !this.exhausted && staminaAllows;
    if (s.crouch) {
      this.stance = Stance.Crouching;
    } else if (wantsSprint) {
      this.stance = Stance.Sprinting;
    } else {
      this.stance = Stance.Standing;
    }

    this.updateStamina(dt);

    const targetSpeed =
      this.stance === Stance.Sprinting
        ? PLAYER.SPRINT_SPEED
        : this.stance === Stance.Crouching
          ? PLAYER.CROUCH_SPEED
          : PLAYER.WALK_SPEED;

    // Desired direction in world space.
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    let dx = s.strafe * cos - s.forward * sin;
    let dz = -s.strafe * sin - s.forward * cos;

    const magnitude = Math.hypot(dx, dz);
    if (magnitude > 0.0001) {
      dx /= magnitude;
      dz /= magnitude;
    }

    const moving = magnitude > 0.0001;
    const step = moving ? targetSpeed * dt : 0;

    let nextX = this.x + dx * step;
    let nextZ = this.z + dz * step;

    if (moving) {
      const resolved = this.resolveCollisions(nextX, nextZ);
      nextX = resolved.x;
      nextZ = resolved.z;
    }

    /**
     * Vertical: gravity and standing on the ground.
     *
     * There is no jump. Nothing in the forest is crossed by jumping — ledges
     * are walked up via STEP_HEIGHT, towers are climbed by ladder — so it only
     * ever existed as a way to make noise by accident, and on a phone it cost
     * the largest button in the thumb cluster. Falling is still modelled:
     * walking off a slope drops you.
     */
    const groundY = this.world.heightAt(nextX, nextZ);

    if (!this.grounded) {
      this.velocityY -= PLAYER.GRAVITY * dt;
    }

    let nextY = this.y + this.velocityY * dt;

    if (nextY <= groundY) {
      nextY = groundY;
      this.velocityY = 0;
      this.grounded = true;
    } else if (nextY - groundY < PLAYER.STEP_HEIGHT && this.velocityY <= 0) {
      // Small rises are stepped over rather than blocking or launching you.
      nextY = groundY;
      this.velocityY = 0;
      this.grounded = true;
    } else {
      this.grounded = false;
    }

    const travelled = Math.hypot(nextX - this.x, nextZ - this.z);
    this.lastSpeed = dt > 0 ? travelled / dt : 0;

    this.x = nextX;
    this.y = nextY;
    this.z = nextZ;

    this.updateCamera(dt, camera, moving ? this.lastSpeed : 0);
  }

  private updateStamina(dt: number): void {
    if (this.stance === Stance.Sprinting) {
      const drain = PLAYER.STAMINA_DRAIN * this.profile.staminaDrain;
      this.stamina = Math.max(0, this.stamina - drain * dt);
      this.sprintCooldown = PLAYER.STAMINA_REGEN_DELAY;
      if (this.stamina <= 0) {
        this.stance = Stance.Standing;
        this.exhausted = true;
      }
    } else {
      this.sprintCooldown = Math.max(0, this.sprintCooldown - dt);
      if (this.sprintCooldown <= 0) this.regen(dt);
    }
  }

  private regen(dt: number): void {
    const rate = PLAYER.STAMINA_REGEN * this.profile.staminaRegen;
    this.stamina = Math.min(PLAYER.STAMINA_MAX, this.stamina + rate * dt);
    // Cleared here as well as in updateGround, so a rest that fills the bar
    // does not leave you locked out of sprinting the moment you step out.
    if (this.exhausted && this.stamina >= PLAYER.STAMINA_EXHAUSTED_RECOVERY) {
      this.exhausted = false;
    }
  }

  /**
   * Recover while at rest — hidden, or hanging motionless on a ladder.
   *
   * The post-sprint delay is skipped deliberately. Both of these are places you
   * go *to* stop, at a real cost: hiding surrenders your view of the forest and
   * runs the breath meter down, and a ladder leaves you pinned in the open with
   * one way off. Making you also sit out a countdown before the bar moves would
   * punish the decision the game wants you to make.
   */
  private rest(dt: number): void {
    this.sprintCooldown = 0;
    this.regen(dt);
  }

  /**
   * Push the player out of any trunk they have walked into.
   *
   * Runs a couple of relaxation passes so wedging between two adjacent trunks
   * resolves cleanly instead of oscillating.
   */
  private resolveCollisions(x: number, z: number): { x: number; z: number } {
    const radius = PLAYER.RADIUS;
    let outX = x;
    let outZ = z;

    for (let pass = 0; pass < 2; pass++) {
      let corrected = false;

      for (const tree of this.world.treesNear(outX, outZ, radius + 1.2)) {
        const dx = outX - tree.x;
        const dz = outZ - tree.z;
        const minDist = radius + tree.radius;
        const distSq = dx * dx + dz * dz;

        if (distSq >= minDist * minDist || distSq < 1e-8) continue;

        const dist = Math.sqrt(distSq);
        const push = (minDist - dist) / dist;
        outX += dx * push;
        outZ += dz * push;
        corrected = true;
      }

      if (!corrected) break;
    }

    // Keep inside the bowl. The rim terrain does most of the work; this is the
    // backstop for the gap between the rim and the true boundary.
    if (!this.world.isInBounds(outX, outZ)) {
      const len = Math.hypot(outX, outZ);
      if (len > 0) {
        const limit = len - 0.5;
        outX = (outX / len) * limit;
        outZ = (outZ / len) * limit;
      }
    }

    return { x: outX, z: outZ };
  }

  // -------------------------------------------------------------------------
  // Climbing
  // -------------------------------------------------------------------------

  private updateClimbing(dt: number, input: Input, camera: THREE.PerspectiveCamera): void {
    const site = this.world.towers[this.towerId];
    if (!site) {
      this.climbing = false;
      this.towerId = -1;
      return;
    }

    const s = input.sample();
    this.stance = Stance.Climbing;

    const base = site.y;
    const top = site.y + TOWERS.HEIGHT;

    this.y = clamp(this.y + s.forward * PLAYER.CLIMB_SPEED * dt, base, top);

    // Stay locked to the ladder rail while climbing.
    const offset = TOWERS.LADDER_WIDTH;
    this.x = site.x + Math.cos(site.rotation) * offset;
    this.z = site.z + Math.sin(site.rotation) * offset;

    /**
     * Stepping off at the bottom returns you to normal movement. The tolerance
     * is generous because the ground under the rail is rarely level with the
     * tower's own base height.
     *
     * Keyboard only. A thumb on a joystick almost never pushes purely
     * sideways — there is always a small vertical component — so on touch
     * this fired the instant a freshly-mounted player touched the stick and
     * threw them off the ladder. There, LET GO is the only way down;
     * the stick can only climb.
     */
    if (!IS_TOUCH_DEVICE && this.y <= base + 0.35 && s.forward < 0) {
      this.dismount();
    }

    // Hanging still on the rail is a rest; hauling yourself up it is not.
    // Climbing does not drain the bar, so the only rule here is that the
    // recovery stops the moment you start moving again.
    if (s.forward === 0) {
      this.rest(dt);
    } else {
      this.sprintCooldown = Math.max(0, this.sprintCooldown - dt);
    }

    this.lastSpeed = Math.abs(s.forward) * PLAYER.CLIMB_SPEED;
    this.updateCamera(dt, camera, 0);
  }

  /**
   * Get off the ladder, wherever you are on it.
   *
   * Bound to Space as an unconditional escape hatch — being stuck on a ladder
   * with no way off is the worst possible bug in a game about running away, so
   * there is always a way down even if something else has gone wrong.
   */
  dismount(): void {
    if (this.towerId < 0 && !this.climbing) return;

    const site = this.world.towers[this.towerId];
    this.climbing = false;
    this.stance = Stance.Standing;
    this.velocityY = 0;

    if (site) {
      // Step off onto the ground beside the mast rather than inside it.
      const out = TOWERS.BASE_RADIUS + 1.5;
      this.x = site.x + Math.cos(site.rotation) * out;
      this.z = site.z + Math.sin(site.rotation) * out;
      this.y = this.world.heightAt(this.x, this.z);
    }

    this.towerId = -1;
  }

  get isClimbing(): boolean {
    return this.climbing;
  }

  get climbProgress(): number {
    if (this.towerId < 0) return 0;
    const site = this.world.towers[this.towerId];
    if (!site) return 0;
    return clamp01((this.y - site.y) / TOWERS.HEIGHT);
  }

  // -------------------------------------------------------------------------
  // Hiding
  // -------------------------------------------------------------------------

  get hidingSpotId(): number {
    return this.hideSpotId;
  }

  /**
   * Duck into a hiding place.
   *
   * The server puts you in the middle of the spot the moment it accepts you —
   * so this is not the client inventing a move, it is the client catching up
   * with one. Skipping it left every other player's screen showing your name
   * over the bush while your own showed a camera orbiting the patch of ground
   * you happened to press E from, with the thing you were supposedly inside
   * sitting off to one side of the shot.
   *
   * Optimistic, like the pose itself: the spot may turn out to be full, which
   * is what leaveHiding and the anchor are for.
   */
  enterHiding(spotId: number): void {
    if (this.hideSpotId === spotId) return;
    // Only the first entry records an anchor — a server correction that moves
    // you between spots must not overwrite the ground you came in from.
    if (this.hideSpotId < 0) this.hideAnchor = { x: this.x, y: this.y, z: this.z };
    this.hideSpotId = spotId;
  }

  /**
   * Climb back out, to where you ducked in from.
   *
   * The way in is by definition somewhere you could stand: it is where you
   * were standing. Anywhere else risks putting you inside the geometry you
   * were just hiding in, and the server restores the same anchor for the same
   * reason — see ForestRoom's ExitHiding.
   */
  leaveHiding(): void {
    if (this.hideSpotId < 0) return;
    this.hideSpotId = -1;
    this.stance = Stance.Standing;
    this.velocityY = 0;

    if (this.hideAnchor) {
      this.x = this.hideAnchor.x;
      this.y = this.hideAnchor.y;
      this.z = this.hideAnchor.z;
      this.hideAnchor = null;
    }
  }

  private updateHidden(dt: number, camera: THREE.PerspectiveCamera): void {
    this.stance = Stance.Hiding;
    this.lastSpeed = 0;
    this.settleIntoSpot(dt);
    this.rest(dt);
    this.updateCamera(dt, camera, 0);
  }

  /**
   * Slide into the middle of the spot.
   *
   * Damped rather than snapped, at the same rate the view pulls back, so the
   * two movements land together and the whole thing reads as being drawn into
   * cover. A snap would jump the camera sideways by the couple of metres you
   * pressed E across, on the one frame the player is least able to place
   * themselves.
   */
  private settleIntoSpot(dt: number): void {
    const spot = this.world.hidingSpots[this.hideSpotId];
    if (!spot) return;

    this.x = damp(this.x, spot.x, CAMERA.TRANSITION_RATE, dt);
    this.y = damp(this.y, spot.y, CAMERA.TRANSITION_RATE, dt);
    this.z = damp(this.z, spot.z, CAMERA.TRANSITION_RATE, dt);
  }

  // -------------------------------------------------------------------------
  // Camera
  // -------------------------------------------------------------------------

  private updateCamera(dt: number, camera: THREE.PerspectiveCamera, speed: number): void {
    const targetEye =
      this.stance === Stance.Crouching
        ? PLAYER.CROUCH_EYE_HEIGHT
        : this.stance === Stance.Hiding
          ? PLAYER.CROUCH_EYE_HEIGHT * 0.85
          : PLAYER.EYE_HEIGHT;

    this.eyeHeight = damp(this.eyeHeight, targetEye, 12, dt);

    // Head bob, scaled to actual speed so it never fights the movement.
    const targetBob = speed > 0.4 ? clamp01(speed / PLAYER.SPRINT_SPEED) : 0;
    this.bobAmount = damp(this.bobAmount, targetBob, 8, dt);
    this.bobPhase += speed * dt * 1.9;

    const bobY = Math.sin(this.bobPhase * 2) * 0.045 * this.bobAmount;
    const bobX = Math.cos(this.bobPhase) * 0.03 * this.bobAmount;

    const eyeX = this.x + bobX;
    const eyeY = this.y + this.eyeHeight + bobY;
    const eyeZ = this.z;

    if (this.cameraBack > 0.001) {
      /**
       * Slide straight back along the look direction.
       *
       * The same forward vector first person is built from — matching
       * forwardFromYaw, extended by pitch — so the view keeps pointing exactly
       * where it did and only the eye moves. Rotation is still written
       * explicitly rather than via lookAt, which would decompose a quaternion
       * back into Euler angles and can flip a near-vertical view by 180.
       */
      const cosPitch = Math.cos(this.pitch);
      const forwardX = -cosPitch * Math.sin(this.yaw);
      const forwardY = Math.sin(this.pitch);
      const forwardZ = -cosPitch * Math.cos(this.yaw);

      const backX = eyeX - forwardX * this.cameraBack;
      const backZ = eyeZ - forwardZ * this.cameraBack;
      // Never let it sink into the hillside behind you while you look up.
      const floor = this.world.heightAt(backX, backZ) + CAMERA.GROUND_CLEARANCE;

      camera.position.set(backX, Math.max(eyeY - forwardY * this.cameraBack, floor), backZ);
    } else {
      camera.position.set(eyeX, eyeY, eyeZ);
    }

    camera.rotation.order = 'YXZ';
    camera.rotation.y = this.yaw;
    camera.rotation.x = this.pitch;
    camera.rotation.z = Math.cos(this.bobPhase) * 0.006 * this.bobAmount;
  }

  /**
   * How far the view currently sits behind the head.
   *
   * Read by the flashlight, which hangs off the camera and would otherwise
   * light the scene from three metres behind the player holding it.
   */
  get cameraOffset(): number {
    return this.cameraBack;
  }

  /**
   * Eye height as a fraction of standing, 1 down to the crouch ratio.
   *
   * Already smoothed, because the camera rides it — so the torch can hang off
   * the same number rather than damping a second copy that could disagree.
   */
  get eyeRatio(): number {
    return this.eyeHeight / PLAYER.EYE_HEIGHT;
  }

  snapshot(): PlayerSnapshot {
    return {
      x: this.x,
      y: this.y,
      z: this.z,
      yaw: this.yaw,
      pitch: this.pitch,
      stance: this.stance,
      speed: this.lastSpeed,
      grounded: this.grounded,
    };
  }
}
